import { useCallback, useEffect, useRef, useState } from 'react'
import { searchProducts } from '@/lib/api-shell'
import type { Product } from '@/lib/types'

interface UseSearchAutocompleteOptions {
  query: string
  minQueryLength?: number
  debounceMs?: number
  enabled?: boolean
}

type FetchSearchAutocompletePageOptions = {
  query: string
  offset: number
  pageSize: number
  signal?: AbortSignal
}

const SEARCH_AUTOCOMPLETE_PAGE_SIZE = 8

export async function fetchSearchAutocompletePage({
  query,
  offset,
  pageSize,
  signal,
}: FetchSearchAutocompletePageOptions) {
  const normalizedPageSize = Math.max(1, Math.trunc(pageSize))
  const normalizedOffset = Math.max(0, Math.trunc(offset))
  // Busca um item adicional para descobrir se existe outra pagina sem
  // depender de uma segunda chamada quando o total for multiplo de 8.
  const results = await searchProducts(query, {
    limit: normalizedPageSize + 1,
    offset: normalizedOffset,
    signal,
  })
  const items = results.slice(0, normalizedPageSize)

  return {
    items,
    nextOffset: normalizedOffset + items.length,
    hasMore: results.length > normalizedPageSize,
  }
}

const appendUniqueProducts = (current: Product[], incoming: Product[]) => {
  if (incoming.length === 0) return current
  const existingIds = new Set(current.map((product) => product.id))
  const uniqueIncoming = incoming.filter((product) => !existingIds.has(product.id))
  return uniqueIncoming.length > 0 ? [...current, ...uniqueIncoming] : current
}

export function useSearchAutocomplete({
  query,
  minQueryLength = 2,
  debounceMs = 300,
  enabled = true,
}: UseSearchAutocompleteOptions) {
  const [suggestions, setSuggestions] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeQueryRef = useRef('')
  const nextOffsetRef = useRef(0)
  const requestSequenceRef = useRef(0)
  const isLoadingRef = useRef(false)
  const isLoadingMoreRef = useRef(false)
  const hasMoreRef = useRef(false)

  useEffect(() => {
    const normalizedQuery = query.trim()
    const requestSequence = ++requestSequenceRef.current
    activeQueryRef.current = normalizedQuery
    nextOffsetRef.current = 0
    hasMoreRef.current = false
    isLoadingMoreRef.current = false

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (!enabled || normalizedQuery.length < minQueryLength) {
      isLoadingRef.current = false
      setSuggestions([])
      setIsLoading(false)
      setIsLoadingMore(false)
      setHasMore(false)
      setError(null)
      return
    }

    isLoadingRef.current = true
    setSuggestions([])
    setIsLoading(true)
    setIsLoadingMore(false)
    setHasMore(false)
    setError(null)

    debounceTimerRef.current = setTimeout(async () => {
      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        const page = await fetchSearchAutocompletePage({
          query: normalizedQuery,
          offset: 0,
          pageSize: SEARCH_AUTOCOMPLETE_PAGE_SIZE,
          signal: controller.signal,
        })
        if (
          requestSequence !== requestSequenceRef.current
          || normalizedQuery !== activeQueryRef.current
        ) return

        setSuggestions(page.items)
        nextOffsetRef.current = page.nextOffset
        hasMoreRef.current = page.hasMore
        setHasMore(page.hasMore)
      } catch (err: any) {
        if (err.name !== 'AbortError' && requestSequence === requestSequenceRef.current) {
          console.error('Autocomplete search failed:', err)
          setError('search.autocomplete_error')
        }
      } finally {
        if (requestSequence === requestSequenceRef.current) {
          isLoadingRef.current = false
          setIsLoading(false)
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null
        }
      }
    }, debounceMs)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
    }
  }, [query, minQueryLength, debounceMs, enabled])

  const loadMore = useCallback(async () => {
    const normalizedQuery = query.trim()
    if (
      !enabled
      || normalizedQuery.length < minQueryLength
      || normalizedQuery !== activeQueryRef.current
      || isLoadingRef.current
      || isLoadingMoreRef.current
      || !hasMoreRef.current
    ) return

    const requestSequence = requestSequenceRef.current
    const controller = new AbortController()
    abortControllerRef.current = controller
    isLoadingMoreRef.current = true
    setIsLoadingMore(true)
    setError(null)

    try {
      const page = await fetchSearchAutocompletePage({
        query: normalizedQuery,
        offset: nextOffsetRef.current,
        pageSize: SEARCH_AUTOCOMPLETE_PAGE_SIZE,
        signal: controller.signal,
      })
      if (
        requestSequence !== requestSequenceRef.current
        || normalizedQuery !== activeQueryRef.current
      ) return

      setSuggestions((current) => appendUniqueProducts(current, page.items))
      nextOffsetRef.current = page.nextOffset
      hasMoreRef.current = page.hasMore
      setHasMore(page.hasMore)
    } catch (err: any) {
      if (err.name !== 'AbortError' && requestSequence === requestSequenceRef.current) {
        console.error('Autocomplete pagination failed:', err)
        setError('search.autocomplete_error')
      }
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }
  }, [enabled, minQueryLength, query])

  return {
    suggestions,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
  }
}
